/**
 * ChatMessages Component
 * 
 * Displays the list of chat messages with auto-scroll functionality.
 * Includes bot messages, user messages, and loading indicator.
 */

import { useRef, useEffect } from 'react';
import BotMessageBubbleChatbot from '@/components/botMessageBubbleChatbot';
import UserMessageBubbleChatbot from '@/components/userMessageBubbleChatbot';
import LoadingMessageBubbleChatbot from '@/components/loadingMessageBubbleChatbot';
import type { ChatMode } from './ChatHeader';

export interface Message {
  id: string;
  content: string;
  from: 'user' | 'bot';
  audio?: string | null;
  timestamp?: Date;
  isStreaming?: boolean;
}

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
  mode: ChatMode;
}

export function ChatMessages({ messages, isLoading, mode }: ChatMessagesProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when user sends a message
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.from === 'user') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <div className="flex flex-col space-y-3">
      {messages.map((message) => {
        const isBot = message.from === 'bot';
        return isBot ? (
          <BotMessageBubbleChatbot
            key={message.id}
            id={message.id}
            content={message.content}
            audio={message.audio}
            timestamp={message.timestamp}
            isStreaming={message.isStreaming}
          />
        ) : (
          <UserMessageBubbleChatbot
            key={message.id}
            content={message.content}
            audio={message.audio}
            timestamp={message.timestamp}
          />
        );
      })}
      
      {/* Loading Indicator */}
      {isLoading && (
        <LoadingMessageBubbleChatbot mode={mode} />
      )}
      <div ref={messagesEndRef} />
    </div>
  );
}

